import { useEffect } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api';
import { OrganizationChoices, type OrganizationChoice } from '../components/OrganizationChoices';
import { ErrorNote, Frame, Spinner } from '../components/ui';

/**
 * Where a platform administrator chooses the organization to work inside.
 *
 * They land here after signing in, and come back through the account menu.
 * It is the one place their session's scope changes: choosing an organization
 * enters it, which that organization's audit log records, and choosing
 * platform administration leaves whichever one they were in. Nothing else in
 * the app enters or leaves on their behalf.
 *
 * Outside the shell, like the sign-in pages, because the shell's masthead and
 * tabs are about a scope the visitor is in the middle of choosing.
 */
const TITLE = 'Choose an organization';

const PLATFORM: OrganizationChoice = { id: null, name: 'Platform administration', slug: null };

function message(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'Could not reach the server. Check your connection and try again.';
}

export function EnterOrganization() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const session = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    // As in the shell: not signed in is an answer, never retried.
    retry: (attempts, error) =>
      !(error instanceof ApiError && error.status === 401) && attempts < 2,
  });
  const signedOut = session.error instanceof ApiError && session.error.status === 401;
  // An organization member has nothing to choose; a bounce beats a dead page.
  const elsewhere = session.data !== undefined && session.data.role !== 'super_admin';

  useEffect(() => {
    if (signedOut) void navigate({ to: '/login' });
    else if (elsewhere) void navigate({ to: '/classes' });
  }, [signedOut, elsewhere, navigate]);

  const organizations = useQuery({
    queryKey: ['organizations'],
    queryFn: api.platform.organizations,
    // Only a platform administrator may ask, so nobody else fires a refusal.
    enabled: session.data?.role === 'super_admin',
  });

  /**
   * Null enters platform administration, which is leaving. Sent even when the
   * session is already outside every organization: the call is idempotent, and
   * it keeps the picker from reasoning about a cached session.
   *
   * The cache is cleared rather than invalidated because everything in it
   * belongs to the scope being left: invalidating would show one
   * organization's lists under another's masthead until the refetch landed.
   * The session is fetched back before navigating so the shell renders its
   * masthead once, correctly.
   */
  const choose = useMutation({
    mutationFn: (id: string | null) => (id === null ? api.platform.exit() : api.platform.enter(id)),
    onSuccess: async (_result, id) => {
      queryClient.clear();
      await queryClient.fetchQuery({ queryKey: ['me'], queryFn: api.me });
      await navigate({ to: id === null ? '/platform' : '/classes' });
    },
  });

  const signOut = useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      queryClient.clear();
      void navigate({ to: '/login' });
    },
  });

  if (signedOut || elsewhere) return null;

  if (session.isLoading || organizations.isLoading) {
    return (
      <Frame title={TITLE}>
        <Spinner label="Loading organizations" />
      </Frame>
    );
  }

  // As in the shell: only 401 means "go and sign in", so everything else that
  // stopped the page loading is shown, with a way forward.
  if (!session.data || !organizations.data) {
    const error = session.error ?? organizations.error;
    return (
      <Frame
        title="Cannot reach the server"
        subtitle="The app is running; its API is not answering."
      >
        <div className="grid gap-4">
          <ErrorNote
            message={
              error instanceof ApiError
                ? `${error.message} (${error.status})`
                : 'No response from the server.'
            }
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void (session.data ? organizations.refetch() : session.refetch())}
          >
            Try again
          </button>
          <Link to="/login" className="btn">
            Go to sign in
          </Link>
        </div>
      </Frame>
    );
  }

  const user = session.data;
  const choices: OrganizationChoice[] = [
    PLATFORM,
    ...organizations.data.organizations.map((org) => ({
      id: org.id,
      name: org.name,
      slug: org.slug,
      // Refused here, as the ledger's Enter was, rather than by the API.
      ...(org.isActive ? {} : { note: 'suspended' }),
    })),
  ];

  return (
    <Frame
      title={TITLE}
      subtitle={`${user.name} · ${user.organization ? user.organization.name : 'platform administration'}`}
    >
      <div className="grid gap-4">
        {choose.error ? <ErrorNote message={message(choose.error)} /> : null}
        <p className="text-sm text-ink-soft">
          Choose where to work. Entering an organization is recorded in its audit log.
        </p>
        <OrganizationChoices
          choices={choices}
          disabled={choose.isPending}
          onChoose={(id) => choose.mutate(id)}
        />
        {organizations.data.organizations.length === 0 ? (
          <p className="text-sm text-ink-faint">
            No organizations yet. Create the first from platform administration.
          </p>
        ) : null}
        <button
          type="button"
          className="text-sm text-ink-soft underline"
          disabled={signOut.isPending}
          onClick={() => signOut.mutate()}
        >
          Sign out
        </button>
      </div>
    </Frame>
  );
}
